import Cookies from "js-cookie";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { useIsMobile } from "@/hooks/use-mobile";

const SIDEBAR_COOKIE_NAME = "sidebarClosed";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
// const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
  isHovered: boolean;
  isMobile: boolean;
  open: boolean;
  openMobile: boolean;
  setIsHovered: (hovered: boolean) => void;
  setOpen: (open: boolean) => void;
  setOpenMobile: (open: boolean) => void;
  state: "collapsed" | "expanded";
  toggleSidebar: () => void;
};

const SidebarContext = createContext<null | SidebarContextProps>(null);

export function useSidebar() {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }

  return context;
}

type SidebarProviderProps = {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

export function SidebarProvider({
  children,
  onOpenChange: setOpenProp,
  open: openProp,
}: SidebarProviderProps) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);
  const [isHovered, _setIsHovered] = useState(false);
  const hideTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  const setIsHovered = useCallback((hovered: boolean) => {
    if (hovered) {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      _setIsHovered(true);
    } else {
      hideTimeoutRef.current = setTimeout(() => _setIsHovered(false), 150);
    }
  }, []);

  const [_open, _setOpen] = useState(() => {
    const cookie = Cookies.get(SIDEBAR_COOKIE_NAME);

    return cookie === "false" ? true : false;
  });

  const open = openProp ?? _open;

  const setOpen = useCallback(
    (value: ((value: boolean) => boolean) | boolean) => {
      const openState = typeof value === "function" ? value(open) : value;

      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      Cookies.set(SIDEBAR_COOKIE_NAME, String(!openState), {
        expires: SIDEBAR_COOKIE_MAX_AGE,
        path: "/",
        sameSite: "lax",
      });
    },
    [setOpenProp, open],
  );

  const toggleSidebar = useCallback(
    () =>
      isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open),
    [isMobile, setOpen, setOpenMobile],
  );

  // Keyboard shortcut: Ctrl/Cmd + B to toggle sidebar
  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (
  //       event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
  //       (event.metaKey || event.ctrlKey)
  //     ) {
  //       event.preventDefault();
  //       toggleSidebar();
  //     }
  //   };

  //   window.addEventListener("keydown", handleKeyDown);

  //   return () => window.removeEventListener("keydown", handleKeyDown);
  // }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      isHovered,
      isMobile,
      open,
      openMobile,
      setIsHovered,
      setOpen,
      setOpenMobile,
      state,
      toggleSidebar,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      isHovered,
    ],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
}
